import { useState, useEffect, useRef, useCallback } from 'react';
import { Avatar, AvatarFallback } from '@/components/shadcn-ui/avatar';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/shadcn-ui/dialog';
import { RocketIcon, FileText, Maximize2, Eye, Link2, Plus } from 'lucide-react';
import { Button } from '@/components/shadcn-ui/button';
import { Input } from '@/components/shadcn-ui/input';
import { Textarea } from '@/components/shadcn-ui/textarea';
import { appAxios } from '@/network/base/appAxios';
import { getGuestId } from '@/utils/getGuestId';
import { useUserStore } from '@/stores/useUser.store';
import { useLocation, useNavigate } from 'react-router-dom';
import { useParams } from 'react-router-dom';
import { useProjectStore } from '@/stores/useProject.store';
import { toast } from 'sonner';
import AuthorizationModal from '../AuthorizationModal';
import CustomerModal from '@/components/CustomerModal';
import AcknowledgmentModal from '@/components/AcknowledgmentModal';
import DocumentUploadModal from '@/components/DocumentUploadModal';
import PermitPaymentModal from '@/components/PermitPaymentModal';
const { setUserRole } = useUserStore.getState();


export function ChatMessage({ message, protectedRoute }) {
  const { auth } = useUserStore.getState();
  const navigate = useNavigate();
  const { permitChatId } = useParams();
  const permitChatGroups = useProjectStore(state => state.permitChatGroups);
  const activePermitChatID = useProjectStore(state => state.activePermitChatID);
  const ensurePermitChatGroupsLoaded = useProjectStore(state => state.ensurePermitChatGroupsLoaded);
  const getProjectIdByPermitChatId = useProjectStore(state => state.getProjectIdByPermitChatId);
  const userRole = useUserStore(state => state.userRole);

  const isUser = message.role === 'user';
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showAlreadySubmittedModal, setShowAlreadySubmittedModal] = useState(false);
  const [projectId, setProjectId] = useState(null);
  const [dataLoading, setDataLoading] = useState(true);
  const [showAuthModal, setshowAuthModal] = useState(false);
  const [linkProjectModalOpen, setLinkProjectModalOpen] = useState(false);
  const [showAckModal, setShowAckModal] = useState(false);
  const [ackData, setAckData] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ name: '', description: '' });
  const [showFileUploadForm, setShowFileUploadForm] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentModalData, setPaymentModalData] = useState(null);
  const projects = useProjectStore(state => state.projects);
  const getProjects = useProjectStore(state => state.getProjects);

  const shownModalForChatRef = useRef(null);

  useEffect(() => {
    if (!protectedRoute) return;
    const loadProjectId = async () => {
      try {
        setDataLoading(true);
        await ensurePermitChatGroupsLoaded();
        const chatId = permitChatId || activePermitChatID;
        if (chatId) {
          const foundProjectId = getProjectIdByPermitChatId(chatId);
          if (foundProjectId) {
            setProjectId(foundProjectId);
            localStorage.setItem(`permitChat_${chatId}_projectId`, foundProjectId);
          } else {
            const cachedProjectId = localStorage.getItem(`permitChat_${chatId}_projectId`);
            if (cachedProjectId) {
              setProjectId(cachedProjectId);
            }
          }
        }
      } catch (error) {
        console.error('Error loading project data:', error);
      } finally {
        setDataLoading(false);
      }
    };

    loadProjectId();
  }, [
    permitChatId,
    activePermitChatID,
    // permitChatGroups?.length,
    ensurePermitChatGroupsLoaded,
    getProjectIdByPermitChatId,
    protectedRoute,
  ]);

  useEffect(() => {
    if (!projectId) return;

    const currentChatId = permitChatId || activePermitChatID;
    if (!currentChatId) return;
    if (shownModalForChatRef.current !== currentChatId) {
      shownModalForChatRef.current = null;
    }
    if (shownModalForChatRef.current === currentChatId) {
      return;
    }

    const submissionStatus = localStorage.getItem(`permit_submission_status_${projectId}_${permitChatId}`);
    const activePermitChat = permitChatGroups?.find(permit => permit._id === currentChatId);
    // const AlreadySubmit = activePermitChat?.permitSubmitted;
    const AlreadySubmit = activePermitChat?.baseFeesPaid && activePermitChat?.permitSubmitted;

    if (submissionStatus) {
      const { submitted: wasSubmitted, timestamp } = JSON.parse(submissionStatus);
      if (wasSubmitted) {
        setSubmitted(true);
        setShowSuccessModal(true);
        shownModalForChatRef.current = currentChatId;
      }
    } else if (AlreadySubmit) {
      setSubmitted(true);
      setShowAlreadySubmittedModal(true);
      shownModalForChatRef.current = currentChatId;
    }
  }, [projectId, permitChatId, activePermitChatID]);

  useEffect(() => {
    const currentChatId = permitChatId || activePermitChatID;
    if (currentChatId && shownModalForChatRef.current !== currentChatId && shownModalForChatRef.current !== null) {
      setSubmitted(false);
      setShowSuccessModal(false);
      setShowAlreadySubmittedModal(false);
    }
  }, [permitChatId, activePermitChatID]);

  useEffect(() => {
    if (linkProjectModalOpen && !showForm) {
      getProjects();
    }
  }, [linkProjectModalOpen, showForm, getProjects]);

  const formatAddress = address => {
    if (!address) return null;

    const { street_number, street_direction, street_name, zipcode } = address;

    return [street_number, street_direction, street_name, zipcode].filter(Boolean).join(' ');
  };

  function convertMetadataToPermit(metadata, userId) {
    return {
      user_id: userId,
      application_id: null,
      scope_of_work: metadata.scope_of_work || null,
      jurisdiction_code: metadata.jurisdiction_code || null,
      portal_address: metadata.address || null,
      project_name: metadata.project_name || null,
      permit_type: metadata.permit_type || null,
      work_description: Array.isArray(metadata.work_description)
        ? metadata.work_description.join(', ')
        : metadata.work_description || null,
      unit: metadata.unit_number?.toString() || null,
      capacity_unit: metadata.ac_unit_count?.toString() || null,
      capacity: metadata.ac_capacity_in_hp || null,
      question_index: metadata.related_permit_type || 0,
      intent_state: metadata.status || null,
      metadata: metadata,
      submit_to_city: false,
    };
  }

  const handleSubmitToCity = async () => {
    try {
      if (!auth) {
        return navigate('/auth');
      }

      if (!userRole) {
        setshowAuthModal(true);
        return;
      }

      if (!projectId) {
        toast.error('Please Link to a Project for Submitting Permit Application');
        return;
      }

      setLoading(true);

      const payload = convertMetadataToPermit(message.metadata || {}, getGuestId());

      const res = await appAxios.post(`/Permit/v1.0/submitPermit/${projectId}/${permitChatId}`, payload);
      if (res.data?.ui_action === 'show_acknowledgement') {
        if (res.data.content) {
          setAckData(res.data.content);
          setShowAckModal(true);
        } else {
          toast.error(res.data.message || 'Acknowledgement required. Please try again.');
        }
        return;
      }
      if (res.data?.status === 'Success') {
        setPaymentModalData({
          permitId: res.data.data._id,
          userId: res.data.data.user_id,
          permitChatId,
        });
        setShowPaymentModal(true);
      }
    } catch (err) {
      if (err?.response?.data?.ui_action === 'show_acknowledgement') {
        if (err.response.data.content) {
          setAckData(err.response.data.content);
          setShowAckModal(true);
        } else {
          toast.error(err.response.data.message || 'Acknowledgement required. Please try again.');
        }
        return;
      }
      if (err?.response?.data?.ui_action === 'show_file_upload_form') {
        setShowFileUploadForm(true);
        return;
      }

      if (err?.response?.status === 403) {
        setshowAuthModal(true);
        return;
      }
      if (err?.response?.status === 400) {
        // setSubmitted(true);

        const submissionData = {
          submitted: true,
          timestamp: new Date().toISOString(),
          message: 'Your Application for Permit Filling has already been submitted. Thank you for using Zermit AI',
        };
        localStorage.setItem(`permit_submission_status_${projectId}_${permitChatId}`, JSON.stringify(submissionData));

        setShowAlreadySubmittedModal(true);
        return;
      }
      console.error('Submit to City error:', err);
    } finally {
      setLoading(false);
    }
  };


  const handleAckSubmit = async () => {
    try {
      const res = await appAxios.post(`/Permit/v1.0/change-acknowledgement-status/${permitChatId}`, {
        modify_type: "acknowledgement_done"
      });
      if (res.data?.status === 'Success') {
        setShowAckModal(false);
        handleSubmitToCity();
      }
    } catch (error) {
      console.error('Failed to submit acknowledgment:', error);
      toast.error('Failed to submit acknowledgment');
    }
  }

  const getUserRole = async () => {
    try {
      const response = await appAxios.get('/Authorization/v1.0/getUserRole')
      const role = response.data.data;
      setUserRole(role);
      return role;
    } catch (error) {
      console.error('Failed to get user role:', error);
      return null;
    }
  }

  const attemptSubmit = () => {
    if (!userRole) {
      setshowAuthModal(true);
      return;
    }

    if (!projectId) {
      setLinkProjectModalOpen(true);
      return;
    }

    handleSubmitToCity();
  };

  const handleAuthSubmit = async () => {
    const role = await getUserRole();
    setshowAuthModal(false);
    setTimeout(() => {
      if (!role) return;
      if (!projectId) {
        setLinkProjectModalOpen(true);
      } else {
        handleSubmitToCity();
      }
    }, 0);
  };

  const handleCreateProject = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await appAxios.post('/Project/v1.0/createProject', {
        projectName: formData.name,
        projectDescription: formData.description
      });
      await getProjects(true);
      setShowForm(false);
      setFormData({ name: '', description: '' });
    } catch (error) {
      console.error('Failed to create project:', error);
      toast.error('Failed to create project');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectProject = async (selectedProjectId) => {
    setLoading(true);
    try {
      await appAxios.post('/Project/v1.0/assignProjectToChat', {
        permitChatId: permitChatId || activePermitChatID,
        projectId: selectedProjectId,
      });
      setProjectId(selectedProjectId);
      setLinkProjectModalOpen(false);
      toast.success('Project linked successfully');
      window.location.reload();
    } catch (error) {
      console.error('Failed to link project:', error);
      toast.error('Failed to link project');
    } finally {
      setLoading(false);
    }
  };

  const handleFileClick = async (fileKey) => {
    try {
      const response = await appAxios.get('/ProtectedPermitChat/v1.0/getAttachmentDocument', { params: { key: fileKey } });
      if (response.data?.status === 'Success') {
        window.open(response.data.data.viewUrl, '_blank');
      }
    } catch (error) {
      console.error('Error processing file:', error);
      toast.error('Failed to process file');
    }
  };

  const handleFetchDocuments = useCallback(async () => {
    const res = await appAxios.get('/documents/required', { params: { permitChatId } });
    return res.data?.data ?? res.data ?? [];
  }, [permitChatId]);

  const handleSubmitDocuments = async uploads => {
    const formData = new FormData();
    Object.entries(uploads).forEach(([docName, files]) => {
      if (files?.[0]) formData.append(docName, files[0]);
    });
    await appAxios.post('/documents/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      params: { permitChatId },
    });
    toast.success('Documents submitted successfully.');
  };

  if (message.type === 'submit_button') {
    return (
      <>
        <div className="flex items-center justify-center w-full py-3">
          {protectedRoute ? (
            dataLoading ? (
              <button
                type="button"
                disabled
                className="flex items-center justify-center bg-[#212121] rounded-full px-4 py-2 border border-[#FEBA08] min-w-64 font-normal cursor-not-allowed opacity-50 transition-colors"
              >
                Loading...
              </button>
            ) : !submitted ? (
              <button
                type="button"
                onClick={attemptSubmit}
                disabled={loading}
                className={`flex items-center justify-center bg-[#212121] rounded-full px-4 py-2 border border-[#FEBA08] min-w-64 font-normal transition-colors ${loading ? 'cursor-not-allowed opacity-50' : 'cursor-pointer hover:bg-[#2a2a2a]'}`}
              >
                {loading ? 'Submitting...' : 'Click to continue'}
              </button>
            ) : (
              <button
                type="button"
                disabled
                className="flex items-center justify-center bg-[#212121] rounded-full px-4 py-2 border border-[#FEBA08] min-w-64 font-normal cursor-not-allowed opacity-50 transition-colors"
              >
                <span className="text-green-400">Submitted!</span>
              </button>
            )
          ) : (
            <button
              type="button"
              onClick={handleSubmitToCity}
              disabled={loading}
              className={`flex items-center justify-center bg-[#212121] rounded-full px-4 py-2 border border-[#FEBA08] min-w-64 font-normal transition-colors ${loading ? 'cursor-not-allowed opacity-50' : 'cursor-pointer hover:bg-[#2a2a2a]'}`}
            >
              {loading ? 'Submitting...' : 'Click to continue'}
            </button>
          )}
        </div>

        {/* Success Modal */}
        {/* <Dialog open={showSuccessModal} onOpenChange={setShowSuccessModal}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="text-center">Submission Successful</DialogTitle>
              <DialogDescription className="text-center pt-4">
                Your Application for Permit Filling is being submitted in Background. Thank you for using Zermit AI.
              </DialogDescription>
            </DialogHeader>
            <div className="flex justify-center pt-4">
              <button
                onClick={() => setShowSuccessModal(false)}
                className="px-6 py-2 bg-black text-white rounded-lg hover:bg-neutral-800 transition"
              >
                Close
              </button>
            </div>
          </DialogContent>
        </Dialog> */}

        {/* <Dialog open={showAlreadySubmittedModal} onOpenChange={setShowAlreadySubmittedModal}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="text-center">Already Submitted</DialogTitle>
              <DialogDescription className="text-center pt-4">
                Your Application for Permit Filling has already been submitted. Thank you for using Zermit AI.
              </DialogDescription>
            </DialogHeader>
            <div className="flex justify-center pt-4">
              <button
                onClick={() => setShowAlreadySubmittedModal(false)}
                className="px-6 py-2 bg-black text-white rounded-lg hover:bg-neutral-800 transition"
              >
                Close
              </button>
            </div>
          </DialogContent>
        </Dialog> */}

        {/* Authorization Modal */}
        <AuthorizationModal
          isOpen={showAuthModal}
          onClose={() => setshowAuthModal(false)}
          onSubmit={() => handleAuthSubmit()}
        />

        {/* Acknowledgment Modal */}
        <AcknowledgmentModal
          isOpen={showAckModal}
          onClose={() => setShowAckModal(false)}
          onSubmit={() => { handleAckSubmit() }}
          generalContent={ackData?.general_acknowledgement}
          acknowledgements={ackData?.acknowledgements}
        />

        {/* Link Project Modal */}
        {userRole === 'contractor' && (
          <CustomerModal isOpen={linkProjectModalOpen} onClose={setLinkProjectModalOpen} permitChatId={permitChatId} />
        )}
        {userRole === 'home_owner' && (
          <Dialog open={linkProjectModalOpen} onOpenChange={setLinkProjectModalOpen}>
            <DialogContent className="sm:max-w-lg dark text-white">
              <DialogHeader>
                <DialogTitle className="text-xl font-baloo-2 my-1">
                  {showForm ? 'Create New Project' : 'Select a Project'}
                </DialogTitle>
                <DialogDescription className="text-sm text-muted-foreground">
                  Link this chat to an existing project or create a new one
                </DialogDescription>
              </DialogHeader>

              {showForm ? (
                <form onSubmit={handleCreateProject} className="space-y-6">
                  <div>
                    <Input
                      placeholder="Project Name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Textarea
                      placeholder="Project Description"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      required
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button type="button" variant="outline" onClick={() => setShowForm(false)} disabled={loading}>
                      Back
                    </Button>
                    <Button type="submit" className="flex-1" disabled={loading}>
                      {loading ? 'Creating...' : 'Create Project'}
                    </Button>
                  </div>
                </form>
              ) : (
                <div className="space-y-3">
                  <Button
                    onClick={() => setShowForm(true)}
                    className="w-full flex items-center gap-2"
                    variant="outline"
                  >
                    <Plus size={16} />
                    Create New Project
                  </Button>

                  <div className="border-t pt-3 max-h-[300px] overflow-y-auto space-y-2 no-scrollbar">
                    {projects.length > 0 ? (
                      projects.map((project) => (
                        <button
                          key={project._id}
                          onClick={() => handleSelectProject(project._id)}
                          disabled={loading}
                          className="
                          w-full text-left
                          p-4 rounded-xl
                          border border-border
                          bg-background
                          hover:bg-accent/60 hover:border-accent
                          focus:outline-none focus:ring-2 focus:ring-primary/40
                          transition
                          disabled:opacity-50 disabled:cursor-not-allowed
                        "
                        >
                          <div className="flex items-start gap-3">
                            {/* Icon */}
                            <div className="flex h-9 w-9 items-center justify-center rounded-md bg-primary/10 text-primary shrink-0">
                              <FileText size={18} />
                            </div>

                            {/* Content */}
                            <div className="flex flex-col gap-1 min-w-0">
                              <div className="font-medium leading-tight truncate">
                                {project.projectName}
                              </div>

                              {project.projectDescription && (
                                <div className="text-sm text-muted-foreground line-clamp-2">
                                  {project.projectDescription}
                                </div>
                              )}
                            </div>
                          </div>
                        </button>
                      ))
                    ) : (
                      <div className="text-center text-sm text-muted-foreground py-4">
                        No projects available
                      </div>
                    )}
                  </div>
                </div>
              )}
            </DialogContent>
          </Dialog>
        )}

        {/* Document Upload Modal */}
        <DocumentUploadModal
          isOpen={showFileUploadForm}
          onClose={() => setShowFileUploadForm(false)}
          onFetchDocuments={handleFetchDocuments}
          onSubmitDocuments={handleSubmitDocuments}
        />
        <PermitPaymentModal
          isOpen={showPaymentModal}
          onClose={() => setShowPaymentModal(false)}
          permitId={paymentModalData?.permitId}
          userId={paymentModalData?.userId}
          permitChatId={paymentModalData?.permitChatId}
        />
      </>
    );
  }

  return (
    <div className={`flex gap-3 ${isUser ? 'flex-row-reverse' : 'flex-row'}`}>
      {!isUser && (
        <Avatar className="size-10 bg-black">
          <AvatarFallback>
            <img src="/kelly.png" alt="Kelly Avatar" className="size-10 object-cover bg-black" />
          </AvatarFallback>
        </Avatar>
      )}

      {isUser ? (
        <div className="flex flex-col gap-2 max-w-[80%]">

          {message?.content?.attachments && message.content.attachments.length > 0 && (
            <div className="flex flex-col gap-2">
              {message.content.attachments.map((file, index) => {
                const isUploading = !file.key || !file.fileUrl;
                return (
                  <button
                    key={index}
                    onClick={() => !isUploading && handleFileClick(file.key)}
                    disabled={isUploading}
                    className={`flex flex-col bg-neutral-900 rounded-lg overflow-hidden transition-colors border border-neutral-700 ${isUploading ? 'opacity-60 cursor-wait' : 'hover:bg-neutral-800 cursor-pointer'}`}
                  >
                    <div className="flex items-center gap-3 px-3 pt-3 pb-2">
                      <div className="flex-shrink-0 w-9 h-9 bg-red-500 rounded-md flex items-center justify-center">
                        <FileText className="w-4 h-4 text-white" />
                      </div>
                      <p className="flex-1 text-sm text-white font-medium truncate text-left">{file.name}</p>
                      {isUploading ? (
                        <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin flex-shrink-0" />
                      ) : (
                        <Link2 className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      )}
                    </div>
                    {file.document_name && (
                      <div className="px-3 py-1.5 border-t border-neutral-700 bg-neutral-800/50">
                        <p className="text-xs italic text-gray-400 truncate text-left">{file.document_name}</p>
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          )}
          {(message?.content?.content && message?.content?.content !== "Attachments uploaded") && (
            <div className="rounded-lg p-3 text-white bg-black">
              <p className="text-sm whitespace-pre-wrap">{message?.content?.content}</p>
            </div>
          )}
        </div>
      ) : (
        <div className="rounded-lg p-3 max-w-[80%] text-white bg-neutral-800">
          <p className="text-sm whitespace-pre-wrap">{message?.content?.content}</p>
        </div>
      )}
    </div>
  );
}